import React, { FC, useEffect, useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { BackDrawerButton, Portal } from 'components/generic';
import { animated, useSpring } from 'react-spring';
import { useWindowDimensions } from 'hooks/menu';
import { useDisableBodyScroll, useDelayUnmount } from 'hooks';
import { useTranslation } from 'react-i18next';
import Parse from 'html-react-parser';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  src: string;
  description: string;
}

const Drawer: FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  src,
  description,
}) => {
  useDisableBodyScroll(isOpen);
  const { t } = useTranslation();
  const isMount = useDelayUnmount(isOpen, 500);
  const { pageHeight } = useWindowDimensions();
  const [{ y }, api] = useSpring(() => ({
    y: pageHeight,
  }));
  const open = () => {
    api.start({
      y: 0,
      immediate: false,
    });
  };
  const close = () => {
    api.start({
      y: pageHeight,
      immediate: false,
    });
    if (isOpen) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      open();
    } else {
      close();
    }
  }, [isOpen]);

  const descLink = useMemo(() => {
    const pattern = /\bhttps?:\/\/\S+/gi;
    if (description) {
      return description.replace(
        pattern,
        '<a href="$&" target="_blank" rel="noopener noreferrer">$&</a>'
      );
    }
    return '';
  }, [description]);

  return (
    <Portal>
      {isMount && (
        <Box>
          <animated.div
            style={{
              position: 'fixed',
              top: '0px',
              left: '0px',
              right: '0px',
              width: '100%',
              height: '150vh',
              background: '#ffffff',
              y,
            }}
          >
            <Box
              sx={{ position: 'fixed', top: '24px', left: '20px' }}
              onClick={close}
            >
              <BackDrawerButton direction="down" />
            </Box>
            <Box
              sx={{
                overflow: 'auto',
                height: '100vh',
                width: '100%',
              }}
            >
              <Box sx={{ maxHeight: '320px', overflow: 'hidden' }}>
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                  component="img"
                  src={src}
                />
              </Box>
              <Box sx={{ padding: '24px 20px 100px 20px' }}>
                <Typography
                  sx={{
                    fontSize: '24px',
                    lineHeight: '28px',
                    fontWeight: '600',
                    marginBottom: '10px',
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    lineHeight: '20px',
                    whiteSpace: 'normal',
                    a: {
                      wordBreak: 'break-word',
                      color: 'primary.main',
                    },
                  }}
                  component="pre"
                >
                  {Parse(descLink) || ''}
                </Typography>
              </Box>
            </Box>
          </animated.div>
          <animated.div
            style={{
              position: 'fixed',
              bottom: '-10px',
              left: '0px',
              right: '0px',
              touchAction: 'none',
              zIndex: '200',
              boxShadow: '0px -1px 8px rgba(0, 0, 0, 0.25)',
              y,
            }}
          >
            <Box
              sx={{
                width: '100%',
                paddingTop: '24px',
                paddingLeft: '20px',
                paddingRight: '20px',
                paddingBottom: 'calc(env(safe-area-inset-bottom) + 34px)',
                zIndex: '1',
                backgroundColor: 'rgba(255, 255, 255, .15)',
                backdropFilter: 'blur(3px)',
              }}
            >
              <Button
                sx={{
                  textTransform: 'inherit',
                  width: '100%',
                  borderRadius: '6px',
                  fontSize: '16px',
                  lineHeight: '16px',
                  height: '100%',
                  padding: '14px 0',
                  color: 'secondary.main',
                  border: '2px solid',
                  '&:hover': {
                    border: '2px solid',
                  },
                  borderColor: 'primary.main',
                  whiteSpace: 'nowrap',
                  boxShadow: 'none',
                  backgroundColor: 'common.white',
                }}
                variant="outlined"
                fullWidth
                onClick={close}
              >
                {t('close')}
              </Button>
            </Box>
          </animated.div>
        </Box>
      )}
    </Portal>
  );
};

export default Drawer;
